/**
 * Authentication Service
 * Manages JWT tokens, user state, and authentication flow
 */

interface User {
  id: string;
  email: string;
  displayName?: string;
  tier: string;
  createdAt: string;
}

interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
}

interface RegisterResponse {
  success: boolean;
  token: string;
  user: User;
}

const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';
const SESSION_TOKEN_KEY = 'sessionToken';

/**
 * Generate a UUID v4
 */
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const authService = {
  /**
   * Store JWT token and user data
   * Clears session token when user authenticates
   */
  setAuth: (token: string, user: User) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    // Clear session token - user is now authenticated
    localStorage.removeItem(SESSION_TOKEN_KEY);
  },

  /**
   * Get stored JWT token
   */
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Get stored user data
   */
  getUser: (): User | null => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Clear authentication (logout)
   */
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Decode JWT to check expiration
   */
  isTokenExpired: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true;
    }
  },

  /**
   * Get token expiration time
   */
  getTokenExpiration: (): number | null => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch {
      return null;
    }
  },

  /**
   * Generate and store a session token for unauthenticated access
   * Session tokens never expire and allow limited access (static QR generation only)
   */
  initializeSessionToken: (): string => {
    let sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!sessionToken) {
      sessionToken = generateUUID();
      localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
    }
    return sessionToken;
  },

  /**
   * Get the current session token
   */
  getSessionToken: (): string | null => {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  },

  /**
   * Clear session token (logout)
   */
  clearSessionToken: () => {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  },
};

export default authService;
